import React, { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NumberInput from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateTimePicker from "@/components/ui/date-time-picker";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Merge, FilePlus2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { STAGE_IDS, TRASH_STATUS, DEFAULT_STATUS } from "@/lib/stages";

const EMPTY = {
  nama: "", no_hp: "", email: "", branch_id: "", sales_id: "", sumber_info: "", kategori_customer: "",
  layanan: "", kota: "", gender: "", kelompok_umur: "", detail_order: "",
  prioritas: "Warm", status: DEFAULT_STATUS, alasan_lost: "", nilai_deal: 0, jumlah_order: 1, follow_up_date: "", created_at: "",
};

// datetime-local inputs need "YYYY-MM-DDTHH:mm" in LOCAL time (no offset) — a plain
// .slice(0,16) on a UTC ISO string would silently shift the displayed time by the
// user's UTC offset, so convert properly instead.
function toLocalDateTimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Same local-time care, but date-only — "Tanggal Masuk" only needs day granularity (a lead
// that walked in Sunday but got typed up Monday should still be dated Sunday), so we don't
// bother asking for a time-of-day the way follow-up does.
function toLocalDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function todayDateInput() {
  return toLocalDateInput(new Date().toISOString());
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-zinc-400 text-[11px] uppercase tracking-wide">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Drop({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-[#18181B] border-white/10 h-10"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-[#18181B] border-white/10">
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function LeadFormDialog({ open, onOpenChange, lead }) {
  const { user } = useAuth();
  const { branches, salesUsers, master } = useMeta();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [mergeChoice, setMergeChoice] = useState(null); // null | "new" | leadId just merged
  const editing = !!lead;

  useEffect(() => {
    if (lead) setForm({ ...EMPTY, ...lead, follow_up_date: toLocalDateTimeInput(lead.follow_up_date), created_at: toLocalDateInput(lead.created_at) });
    else setForm({ ...EMPTY, created_at: todayDateInput() });
    setMergeChoice(null);
  }, [lead, open]);

  useEffect(() => {
    if (!open) { setDupes([]); return; }
    const nama = (form.nama || "").trim();
    const hp = (form.no_hp || "").trim();
    if (nama.length < 3 && hp.length < 5) { setDupes([]); return; }
    const t = setTimeout(async () => {
      try {
        const p = new URLSearchParams();
        if (nama) p.set("nama", nama);
        if (hp) p.set("no_hp", hp);
        if (lead?.lead_id) p.set("exclude_id", lead.lead_id);
        const { data } = await api.get(`/leads/check-duplicate?${p.toString()}`);
        setDupes(data.matches || []);
      } catch { setDupes([]); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.nama, form.no_hp, open]);

  // A trashed match we can actually act on (lead_id visible — not redacted from another sales' data).
  const trashMatch = !editing ? dupes.find((d) => d.status === TRASH_STATUS && d.lead_id) : null;
  const blockedByTrashMatch = !!trashMatch && mergeChoice === null;

  const mergeInto = async (targetLeadId) => {
    setBusy(true);
    try {
      await api.patch(`/leads/${targetLeadId}/restore`);
      const payload = { ...parseReminderDays(form), nilai_deal: Math.max(0, Number(form.nilai_deal) || 0), jumlah_order: Math.max(1, Number(form.jumlah_order) || 1),
        follow_up_date: form.follow_up_date ? new Date(form.follow_up_date).toISOString() : null };
      await api.put(`/leads/${targetLeadId}`, payload);
      toast.success("Digabung ke data lama");
      qc.invalidateQueries();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // The text field is just for comfortable comma-separated typing — parse it into the
  // actual int[] the backend expects, and never send the raw text field itself.
  const parseReminderDays = (f) => {
    const { custom_reminder_days_text, ...rest } = f;
    if (custom_reminder_days_text === undefined) return rest;
    const days = custom_reminder_days_text.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isInteger(n) && n > 0);
    return { ...rest, custom_reminder_days: days };
  };
  const mv = (t) => (master[t] || []).map((m) => ({ value: m.value, label: m.value }));

  const sapaan = (form.nama || "").startsWith("Ibu.") ? "Ibu." : "Bpk.";
  const namaOnly = (form.nama || "").replace(/^((Bpk\.|Ibu\.)\s*)/, "");
  const setSapaan = (sp) => set("nama")(`${sp} ${namaOnly}`.trim());
  const setNamaOnly = (v) => set("nama")(v ? `${sapaan} ${v}` : sapaan);

  const submit = async () => {
    if (blockedByTrashMatch) { toast.error("Pilih dulu: gabung ke data lama atau buat baru"); return; }
    const nm = (form.nama || "").trim();
    if (!(nm.startsWith("Bpk.") || nm.startsWith("Ibu."))) { toast.error('Nama harus diawali "Bpk." atau "Ibu."'); return; }
    if (nm.replace(/^(Bpk\.|Ibu\.)\s*/, "").trim().length < 2) { toast.error("Lengkapi nama customer"); return; }
    const hp = (form.no_hp || "").trim();
    if (hp && !/^\+?\d{10,}$/.test(hp)) { toast.error("No HP hanya angka & minimal 10 digit (tanpa tanda hubung/spasi)"); return; }
    setBusy(true);
    const payload = { ...parseReminderDays(form), nilai_deal: Math.max(0, Number(form.nilai_deal) || 0), jumlah_order: Math.max(1, Number(form.jumlah_order) || 1),
      follow_up_date: form.follow_up_date ? new Date(form.follow_up_date).toISOString() : null,
      // Date-only input, kept at local midnight — only the day matters ("masuk hari Minggu,
      // baru sempat diketik Senin" should still be dated Minggu), not a specific time.
      created_at: form.created_at ? new Date(`${form.created_at}T00:00:00`).toISOString() : undefined };
    try {
      if (editing) await api.put(`/leads/${lead.lead_id}`, payload);
      else await api.post("/leads", payload);
      toast.success(editing ? "Lead diperbarui" : "Lead ditambahkan");
      qc.invalidateQueries();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f12] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto hide-scrollbar">
        <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Edit Lead" : "Tambah Lead Baru"}</DialogTitle>
          <DialogDescription className="text-zinc-500">{editing ? "Perbarui data lead." : "Isi data lead baru."}{form.locked ? " Nilai deal terkunci — buka kunci di detail lead untuk mengubah." : ""}</DialogDescription>
        </DialogHeader>
        {trashMatch && (
          <div data-testid="dup-trash-warning" className="rounded-xl border border-orange-500/40 bg-orange-500/10 p-3 text-sm">
            <div className="flex items-center gap-1.5 text-orange-400 font-medium mb-1.5"><AlertTriangle size={15} />Data ini sudah pernah ada di Kotak Sampah</div>
            <div className="text-xs text-zinc-300 mb-2">{trashMatch.nama} · {trashMatch.no_hp || "-"} · masuk {fmtDate(trashMatch.created_at)}</div>
            {mergeChoice === null ? (
              <div className="flex gap-2">
                <Button type="button" data-testid="dup-merge-btn" onClick={() => mergeInto(trashMatch.lead_id)} disabled={busy}
                  className="flex-1 h-9 bg-orange-600 hover:bg-orange-700 text-xs font-medium"><Merge size={14} className="mr-1.5" />Gabung ke Data Lama</Button>
                <Button type="button" data-testid="dup-new-btn" onClick={() => setMergeChoice("new")} disabled={busy} variant="outline"
                  className="flex-1 h-9 border-white/10 text-xs font-medium"><FilePlus2 size={14} className="mr-1.5" />Buat Baru</Button>
              </div>
            ) : (
              <div className="text-[11px] text-emerald-400">✓ Dipilih: lanjut sebagai data baru terpisah.</div>
            )}
          </div>
        )}
        {dupes.filter((d) => d !== trashMatch).length > 0 && (
          <div data-testid="dup-warning" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            <div className="flex items-center gap-1.5 text-amber-400 font-medium mb-1.5"><AlertTriangle size={15} />Mirip data yang sudah ada ({dupes.filter((d) => d !== trashMatch).length})</div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {dupes.filter((d) => d !== trashMatch).map((d) => (
                <div key={d.lead_id || `${d.nama}-${d.created_at}`} className="flex justify-between gap-2 text-xs">
                  <span className="truncate text-zinc-300">{d.nama} · {d.no_hp || "-"}</span>
                  <span className="text-zinc-500 shrink-0">{d.status} · {d.sales_name} · {fmtDate(d.created_at)}</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1.5">Cek dulu — kalau memang sama, hindari input ganda.</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Nama (wajib Bpk./Ibu.)">
            <div className="flex gap-2">
              <Select value={sapaan} onValueChange={setSapaan}>
                <SelectTrigger data-testid="lead-sapaan" className="w-[76px] shrink-0 bg-[#18181B] border-white/10 h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-white/10"><SelectItem value="Bpk.">Bpk.</SelectItem><SelectItem value="Ibu.">Ibu.</SelectItem></SelectContent>
              </Select>
              <Input data-testid="lead-nama" value={namaOnly} onChange={(e) => setNamaOnly(e.target.value)} placeholder="Nama customer" className="bg-[#18181B] border-white/10 h-10 flex-1 min-w-0" />
            </div>
          </Field></div>
          <Field label="No. HP"><Input data-testid="lead-hp" value={form.no_hp} onChange={(e) => set("no_hp")(e.target.value)} placeholder="08128889999" className="bg-[#18181B] border-white/10 h-10" /><div className="text-[10px] text-zinc-600 mt-1">Angka saja, min 10 digit, tanpa tanda hubung</div></Field>
          {/* No. HP dan Email di sini adalah satu-satunya sumber data yang dipakai tombol
              kirim WA/Email di semua dokumen milik lead ini (lihat sendDocWa/sendDocEmail di
              LeadDetailSheet.jsx) — jadi begitu diedit di sini, semua dokumen ikut terkirim
              ke nomor/email yang benar tanpa perlu dibuka ulang satu-satu. */}
          <Field label="Email"><Input type="email" data-testid="lead-email" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="client@email.com" className="bg-[#18181B] border-white/10 h-10" /></Field>
          {user?.role !== "sales" && (
            <>
              <Field label="Cabang"><Drop testid="lead-branch" value={form.branch_id} onChange={set("branch_id")} options={branches.map((b) => ({ value: b.branch_id, label: b.name }))} placeholder="Pilih cabang" /></Field>
              <Field label="Sales"><Drop testid="lead-sales" value={form.sales_id} onChange={set("sales_id")} options={salesUsers.map((s) => ({ value: s.user_id, label: s.name }))} placeholder="Pilih sales" /></Field>
            </>
          )}
          <Field label="Sumber Info"><Drop value={form.sumber_info} onChange={set("sumber_info")} options={mv("sumber_info")} placeholder="Sumber" /></Field>
          <Field label="Kategori"><Drop value={form.kategori_customer} onChange={set("kategori_customer")} options={mv("kategori_customer")} placeholder="Kategori" /></Field>
          <Field label="Layanan"><Drop value={form.layanan} onChange={set("layanan")} options={mv("layanan")} placeholder="Layanan" /></Field>
          <Field label="Kota"><Drop value={form.kota} onChange={set("kota")} options={mv("kota")} placeholder="Kota" /></Field>
          <Field label="Gender"><Drop value={form.gender} onChange={set("gender")} options={mv("gender")} placeholder="Gender" /></Field>
          <Field label="Kelompok Umur"><Drop value={form.kelompok_umur} onChange={set("kelompok_umur")} options={mv("kelompok_umur")} placeholder="Umur" /></Field>
          <Field label="Prioritas"><Drop value={form.prioritas} onChange={set("prioritas")} options={[{value:"Hot",label:"Hot"},{value:"Warm",label:"Warm"},{value:"Cold",label:"Cold"}]} placeholder="Prioritas" /></Field>
          <Field label="Status"><Drop testid="lead-status" value={form.status} onChange={set("status")} options={[...STAGE_IDS.map((s) => ({ value: s, label: s })), { value: TRASH_STATUS, label: "Tidak Jelas (Sampah)" }]} placeholder="Status" /></Field>
          <Field label="Nilai Deal (IDR)"><NumberInput data-testid="lead-nilai" disabled={!!form.locked} value={form.nilai_deal} onChange={(e) => set("nilai_deal")(e.target.value)} className="bg-[#18181B] border-white/10 h-10 disabled:opacity-50" /></Field>
          <Field label="Jumlah Order"><Input data-testid="lead-jumlah" type="number" min="1" value={form.jumlah_order} onChange={(e) => set("jumlah_order")(e.target.value)} className="bg-[#18181B] border-white/10 h-10" /></Field>
          <Field label="Tanggal Masuk">
            <Input data-testid="lead-created-at" type="date" value={form.created_at} onChange={(e) => set("created_at")(e.target.value)} className="bg-[#18181B] border-white/10 h-10" />
          </Field>
          <Field label="Follow-up Berikutnya"><DateTimePicker testId="lead-followup" value={form.follow_up_date} onChange={(v) => set("follow_up_date")(v)} className="h-10" /></Field>
          {form.follow_up_date && (
            <Field label="Reminder Tambahan (hari sebelum, pisah koma)">
              <Input placeholder="Otomatis: H-5, H-3, H-2, H-1. mis. 7, 10" value={form.custom_reminder_days_text ?? (form.custom_reminder_days || []).join(", ")}
                onChange={(e) => set("custom_reminder_days_text")(e.target.value)} className="bg-[#18181B] border-white/10 h-10" />
            </Field>
          )}
          {form.status === "Lost" && (
            <div className="col-span-2"><Field label="Alasan Lost"><Drop value={form.alasan_lost} onChange={set("alasan_lost")} options={mv("alasan_lost")} placeholder="Alasan" /></Field></div>
          )}
          <div className="col-span-2"><Field label="Detail Order"><Textarea value={form.detail_order} onChange={(e) => set("detail_order")(e.target.value)} className="bg-[#18181B] border-white/10" rows={2} /></Field></div>
        </div>
        <Button data-testid="lead-save" onClick={submit} disabled={busy || blockedByTrashMatch} className="w-full h-11 mt-2 bg-gradient-to-r from-blue-700 to-blue-900 font-semibold disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={18} /> : (editing ? "Simpan Perubahan" : "Tambah Lead")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
